import React from 'react'
import { isSignal } from '../../util/signals'

export type Props = AnyLiteral
export type FC<P extends Props = any> = (props: P) => any
// eslint-disable-next-line @typescript-eslint/naming-convention
export type FC_withDebug = FC & { DEBUG_contentComponentName?: string }

export enum VirtualElementTypesEnum {
  Empty,
  Text,
  Tag,
  Component,
  Fragment,
}

interface VirtualElementEmpty {
  type: VirtualElementTypesEnum.Empty
  target?: Node
}

interface VirtualElementText {
  type: VirtualElementTypesEnum.Text
  target?: Node
  value: string
}

export interface VirtualElementTag {
  type: VirtualElementTypesEnum.Tag
  target?: HTMLElement
  tag: string
  props: Props
  children: VirtualElementChildren
}

export interface VirtualElementComponent {
  type: VirtualElementTypesEnum.Component
  componentInstance: ComponentInstance
  props: Props
  children: VirtualElementChildren
}

export interface VirtualElementFragment {
  type: VirtualElementTypesEnum.Fragment
  children: VirtualElementChildren
}

export type StateHookSetter<T> = (newValue: ((current: T) => T) | T) => void

interface ComponentInstance {
  id: number
  $element: VirtualElementComponent
  Component: FC
  name: string
  props: Props
  renderedValue?: any
  isMounted: boolean
  hooks: {
    state: {
      cursor: number
      byCursor: {
        value: any
        nextValue: any
        setter: StateHookSetter<any>
      }[]
    }
    effects: {
      cursor: number
      byCursor: {
        dependencies?: readonly any[]
        schedule: NoneToVoidFunction
        cleanup?: NoneToVoidFunction
        releaseSignals?: NoneToVoidFunction
      }[]
    }
    memos: {
      cursor: number
      byCursor: {
        value: any
        dependencies: any[]
      }[]
    }
    refs: {
      cursor: number
      byCursor: {
        current: any
      }[]
    }
  }
  prepareForFrame?: () => void
  forceUpdate?: () => void
  onUpdate?: () => void
}

export type VirtualElement =
  | VirtualElementEmpty
  | VirtualElementText
  | VirtualElementTag
  | VirtualElementComponent
  | VirtualElementFragment
export type VirtualElementParent = VirtualElementTag | VirtualElementComponent | VirtualElementFragment
export type VirtualElementChildren = VirtualElement[]
export type VirtualElementReal = Exclude<VirtualElement, VirtualElementComponent | VirtualElementFragment>

// Compatibility with JSX types
export type TeactNode = React.ReactNode

export default React

export { useCallback, useId, useMemo, useRef, useState } from 'react'

const typedMemo = React.memo as <T extends React.ComponentType<any>>(component: T) => T

export { typedMemo as memo }

let pendingEarlyEffects = new Set<NoneToVoidFunction>()
let pendingEffects = new Set<NoneToVoidFunction>()
let pendingLayoutEffects = new Set<NoneToVoidFunction>()
let triggerEffectsCallback: NoneToVoidFunction | undefined

export const addEffect = (effect: NoneToVoidFunction, early = false) => {
  if (early) {
    pendingEarlyEffects.add(effect)
  } else {
    pendingEffects.add(effect)
  }
  triggerEffectsCallback?.()
}
export const addLayoutEffect = (effect: NoneToVoidFunction) => {
  pendingLayoutEffects.add(effect)
  triggerEffectsCallback?.()
}

const forceUpdateReducer = () => ({})

const attempt = (cb: NoneToVoidFunction) => {
  try {
    cb()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in effect', cb, error)
  }
}

export const EffectsRunner = React.memo(() => {
  const [, forceUpdate] = React.useReducer(forceUpdateReducer, {})

  React.useEffect(() => {
    if (triggerEffectsCallback) {
      throw new Error('EffectsRunner already mounted')
    }

    triggerEffectsCallback = forceUpdate

    return () => {
      triggerEffectsCallback = undefined
    }
  }, [])

  React.useLayoutEffect(() => {
    const currentEffects = pendingLayoutEffects
    pendingLayoutEffects = new Set()
    currentEffects.forEach((effect) => attempt(effect))
  })
  React.useEffect(() => {
    const currentEarlyEffects = pendingEarlyEffects
    pendingEarlyEffects = new Set()
    currentEarlyEffects.forEach((effect) => attempt(effect))

    const currentEffects = pendingEffects
    pendingEffects = new Set()
    currentEffects.forEach((effect) => attempt(effect))
  })

  return null
})

const useBaseEffect = (layout: boolean, effect: React.EffectCallback, dependencies?: React.DependencyList) => {
  const effectRef = React.useRef(effect)
  React.useInsertionEffect(() => {
    effectRef.current = effect
  })

  React.useEffect(() => {
    let mounted = true

    function setupSignals() {
      const cleanups = dependencies?.filter(isSignal).map((signal) =>
        signal.subscribe(() => {
          const wrappedEffect = () => {
            if (mounted) {
              effectRef.current()
            }
          }
          if (layout) {
            addLayoutEffect(wrappedEffect)
          } else {
            addEffect(wrappedEffect)
          }
        }),
      )

      return () => cleanups?.forEach((cleanup) => cleanup())
    }

    const cleanup = setupSignals()

    return () => {
      cleanup()
      mounted = false
    }
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, [])

  const useEffect = layout ? React.useLayoutEffect : React.useEffect

  // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  useEffect(effect, dependencies)
}
export const useEffect: typeof React.useEffect = (effect, dependencies) => {
  return useBaseEffect(false, effect, dependencies)
}
export const useLayoutEffect: typeof React.useLayoutEffect = (effect, dependencies) => {
  return useBaseEffect(true, effect, dependencies)
}
